import { supabase } from '@/integrations/supabase/client';
import type {
  ProductPinRow,
  ProductReviewIterationRow,
  ProductCapabilityRow,
  ProductIntegrationRow,
  ProductRowExtended,
} from '@/types/supabase-extensions';

// Products
export async function getProductBySlug(slug: string) {
  const { data, error } = await supabase
    .from('products')
    .select('*')
    .eq('slug', slug)
    .maybeSingle();
  if (error) throw error;
  return data as any;
}

export async function getProductById(productId: string) {
  const { data, error } = await supabase
    .from('products')
    .select('*')
    .eq('id', productId)
    .maybeSingle();
  if (error) throw error;
  return data as any;
}

export async function getProductByVendorToken(token: string) {
  const { data, error } = await (supabase as any)
    .from('products')
    .select('*')
    .eq('vendor_review_token', token)
    .maybeSingle();
  if (error) throw error;
  return data as any;
}

export async function updateProduct(id: string, updates: Record<string, any>) {
  const { error } = await supabase
    .from('products')
    .update(updates)
    .eq('id', id);
  if (error) throw error;
}

// Review Iterations
export async function getReviewIteration(productId: string, iterationNumber: number) {
  const { data, error } = await (supabase as any)
    .from('product_review_iterations')
    .select('*')
    .eq('product_id', productId)
    .eq('iteration_number', iterationNumber)
    .maybeSingle();
  if (error) throw error;
  return data as ProductReviewIterationRow | null;
}

export async function createReviewIteration(productId: string, iterationNumber: number) {
  const { error } = await (supabase as any)
    .from('product_review_iterations')
    .insert({
      product_id: productId,
      iteration_number: iterationNumber,
      sent_to_vendor_at: new Date().toISOString(),
    });
  if (error) throw error;
}

export async function updateReviewIteration(
  productId: string,
  iterationNumber: number,
  updates: Record<string, any>
) {
  const { error } = await (supabase as any)
    .from('product_review_iterations')
    .update(updates)
    .eq('product_id', productId)
    .eq('iteration_number', iterationNumber);
  if (error) throw error;
}

// Pins
export async function getProductPins(productId: string, iterationNumber?: number) {
  let query = (supabase as any)
    .from('product_pins')
    .select('*')
    .eq('product_id', productId)
    .order('created_at', { ascending: true });
  
  if (iterationNumber !== undefined) {
    query = query.eq('iteration_number', iterationNumber);
  }
  
  const { data, error } = await query;
  if (error) throw error;
  return (data || []) as ProductPinRow[];
}

export async function createProductPin(pin: {
  product_id: string;
  iteration_number: number;
  x_percent: number;
  y_percent: number;
  text: string;
  author_type: string;
  author_name?: string;
  author_email?: string;
}) {
  const { data, error } = await (supabase as any)
    .from('product_pins')
    .insert(pin)
    .select()
    .single();
  if (error) throw error;
  return data as ProductPinRow;
}

export async function updatePin(pinId: string, updates: Record<string, any>) {
  const { error } = await (supabase as any)
    .from('product_pins')
    .update(updates)
    .eq('id', pinId);
  if (error) throw error;
}

export async function incrementIterationPinsCount(productId: string, iterationNumber: number) {
  const iteration = await getReviewIteration(productId, iterationNumber);
  if (iteration) {
    await updateReviewIteration(productId, iterationNumber, {
      pins_count: (iteration.pins_count || 0) + 1,
    });
  }
}

// Capabilities
export async function getProductCapabilities(productId: string) {
  const { data, error } = await (supabase as any)
    .from('product_capabilities')
    .select('*')
    .eq('product_id', productId);
  if (error) throw error;
  return (data || []) as ProductCapabilityRow[];
}

// Integrations
export async function getProductIntegrations(productId: string) {
  const { data, error } = await (supabase as any)
    .from('product_integrations')
    .select('*, integrates_with:products!integrates_with_product_id(*)')
    .eq('product_id', productId);
  if (error) throw error;
  return (data || []) as ProductIntegrationRow[];
}
